import { Box, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalActivityOutlinedIcon from '@mui/icons-material/LocalActivityOutlined';
import { NavLink, useLocation } from 'react-router-dom';
import type { LearningModality, SiteLearningWorkspace } from '@common/domain/learning-workspace';
import AccountMenu from '@frontend/components/AccountMenu/AccountMenu';
import { AdminSidebarItems } from '@frontend/components/AdminSidebarItems';
import { config } from '../config';
import './LearningSidebar.css';

const MODALITY_ICON: Record<LearningModality, JSX.Element> = {
  textbook: <MenuBookRoundedIcon />,
  chat: <ChatBubbleOutlineRoundedIcon />,
  avatar: <RecordVoiceOverRoundedIcon />,
};

const MODALITY_LABEL: Record<LearningModality, string> = {
  textbook: 'Textbook',
  chat: 'Chat',
  avatar: 'Avatar',
};

const CUSTOM_TAB_ICON: Record<string, JSX.Element> = {
  about: <InfoOutlinedIcon />,
  activities: <LocalActivityOutlinedIcon />,
};

const ALL_MODALITIES: LearningModality[] = ['textbook', 'chat', 'avatar'];

export interface LearningSidebarProps {
  workspace: SiteLearningWorkspace;
}

export function LearningSidebar({ workspace }: LearningSidebarProps) {
  const { pathname } = useLocation();

  const isAdminPath = pathname === '/admin' || pathname.startsWith('/admin/');
  const showWorkspaceMaterials = workspace.enabled && workspace.materials.length > 0;

  if (!isAdminPath && !showWorkspaceMaterials) {
    return null;
  }

  // Lincoln uses a single core material (the Lyceum Address). Render all 3 modalities
  // (Textbook, Chat, Avatar) pointing to the Lyceum Address material.
  const activeMaterial = workspace.materials[0];

  return (
    <Box
      component="nav"
      className="learning-sidebar"
      aria-label={isAdminPath && !showWorkspaceMaterials ? "Admin navigation" : "Learning modules"}
    >
      <List className="learning-sidebar__list" disablePadding>
        {showWorkspaceMaterials && activeMaterial && ALL_MODALITIES.map((modality) => {
          const to = `/learn/${workspace.persona.slug}/${modality}/${activeMaterial.slug}`;
          const basePath = `/learn/${workspace.persona.slug}/${modality}`;
          const isActive = pathname === to || pathname === basePath;
          return (
            <ListItemButton
              key={modality}
              component={NavLink}
              to={to}
              className={`learning-sidebar__item${isActive ? ' is-active' : ''}`}
              title={activeMaterial.title}
            >
              <ListItemIcon className="learning-sidebar__icon">
                {MODALITY_ICON[modality]}
              </ListItemIcon>
              <ListItemText primary={MODALITY_LABEL[modality]} />
            </ListItemButton>
          );
        })}
        {showWorkspaceMaterials && (config.customTabs || []).map((tab) => {
          const isActive = pathname === tab.path;
          return (
            <ListItemButton
              key={tab.path}
              component={NavLink}
              to={tab.path}
              className={`learning-sidebar__item${isActive ? ' is-active' : ''}`}
              title={tab.label}
            >
              <ListItemIcon className="learning-sidebar__icon">
                {CUSTOM_TAB_ICON[tab.icon]}
              </ListItemIcon>
              <ListItemText primary={tab.label} />
            </ListItemButton>
          );
        })}
        <AdminSidebarItems showDivider={showWorkspaceMaterials} showConversationResults={true} />
      </List>
      <Box sx={{ flex: 1 }} />
      <AccountMenu showSettings={false} />
    </Box>
  );
}
